import { useEffect, useState, useCallback } from "react";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CertifiedBadge } from "@/components/CertifiedBadge";
import { FreshnessGauge } from "@/components/FreshnessGauge";
import HostAlertsFeed from "@/components/HostAlertsFeed";
import HostMarketingKit from "@/components/HostMarketingKit";
import HostGrowthTile from "@/components/HostGrowthTile";
import HostReferralTile from "@/components/HostReferralTile";
import { Dialog as BadgeDialog, DialogContent as BadgeDialogContent, DialogHeader as BadgeDialogHeader, DialogTitle as BadgeDialogTitle, DialogTrigger as BadgeDialogTrigger } from "@/components/ui/dialog";
import { Plus, MapPin, ShieldCheck, AlertTriangle, Repeat, Trash2, ImagePlus, Sparkles } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", address: "", city: "", country: "", description: "",
  booking_url: "", booking_platform: "airbnb", price_per_night: "" };

export default function HostDashboard() {
  const [items, setItems] = useState([]);
  const [certs, setCerts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const [pr, cr] = await Promise.all([api.get("/properties/mine"), api.get("/certifications/mine")]);
    setItems(pr.data); setCerts(cr.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createProp = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form, price_per_night: form.price_per_night ? Number(form.price_per_night) : null };
      await api.post("/properties", payload);
      toast.success("Property created");
      setForm(emptyForm); setOpen(false); await load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setCreating(false); }
  };

  const deleteProp = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try { await api.delete(`/properties/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image is too large (max 8MB)");
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/properties/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const openCheckoutInPreOpenedWindow = (win, url) => {
    if (win && !win.closed) {
      win.location.href = url;
      toast.success("Checkout opened in a new tab");
    } else {
      try { window.top.location.href = url; }
      catch { window.location.href = url; }
    }
  };

  const applyCert = (property_id, tier) => {
    const win = window.open("", "_blank");
    api.post("/certifications", { property_id, tier })
      .then(({ data: cert }) => {
        const lookup = tier === "premium" ? "cert_premium" : "cert_standard";
        return api.post("/payments/checkout", {
          lookup_key: lookup, origin_url: window.location.origin,
          property_id, certification_id: cert.id,
        });
      })
      .then(({ data }) => openCheckoutInPreOpenedWindow(win, data.checkout_url))
      .catch((e) => { if (win && !win.closed) win.close(); toast.error(fmtErr(e)); });
  };

  const subscribe = (lookup_key) => {
    const win = window.open("", "_blank");
    api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin })
      .then(({ data }) => openCheckoutInPreOpenedWindow(win, data.checkout_url))
      .catch((e) => { if (win && !win.closed) win.close(); toast.error(fmtErr(e)); });
  };

  const certForProp = (pid) => certs.find((c) => c.property_id === pid);
  const stats = {
    total: items.length,
    certified: items.filter((p) => p.status === "certified").length,
    pending: certs.filter((c) => c.status === "pending").length,
    avgScore: items.length ? Math.round(items.reduce((s,p)=>s+(p.freshness_score||0),0)/items.length) : 0,
  };

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Host Dashboard</div>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight text-[#0A1128]">Your properties.</h1>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => subscribe("monitor_monthly")} data-testid="subscribe-monthly" className="rounded-full">
              <Repeat className="w-4 h-4 mr-1" /> Subscribe Monthly · $29
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-property" className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white">
                  <Plus className="w-4 h-4 mr-1" /> Add Property
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-serif text-2xl">New Property</DialogTitle></DialogHeader>
                <form onSubmit={createProp} className="space-y-3 mt-2" data-testid="new-property-form">
                  <div><Label>Name</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="prop-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>City</Label><Input required value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} data-testid="prop-city" /></div>
                    <div><Label>Country</Label><Input required value={form.country} onChange={(e)=>setForm({...form, country: e.target.value})} data-testid="prop-country" /></div>
                  </div>
                  <div><Label>Address</Label><Input required value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} data-testid="prop-address" /></div>
                  <div><Label>Description</Label><Textarea value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} data-testid="prop-description" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Platform</Label>
                      <Select value={form.booking_platform} onValueChange={(v)=>setForm({...form, booking_platform: v})}>
                        <SelectTrigger data-testid="prop-platform"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="airbnb">Airbnb</SelectItem>
                          <SelectItem value="vrbo">Vrbo</SelectItem>
                          <SelectItem value="booking">Booking.com</SelectItem>
                          <SelectItem value="hotel">Hotel</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Price / night</Label><Input type="number" value={form.price_per_night} onChange={(e)=>setForm({...form, price_per_night: e.target.value})} data-testid="prop-price" /></div>
                  </div>
                  <div><Label>Booking URL</Label><Input value={form.booking_url} onChange={(e)=>setForm({...form, booking_url: e.target.value})} data-testid="prop-url" /></div>
                  <Button type="submit" disabled={creating} data-testid="prop-submit" className="w-full rounded-full bg-[#0A1128] text-white hover:bg-[#1A2A3A]">
                    {creating ? "Saving…" : "Create Property"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Properties", v: stats.total },
            { l: "Certified", v: stats.certified },
            { l: "Pending review", v: stats.pending },
            { l: "Avg. score", v: stats.avgScore },
          ].map((s) => (
            <div key={s.l} className="bg-white rounded-2xl p-6 border border-slate-200" data-testid={`stat-${s.l}`}>
              <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500">{s.l}</div>
              <div className="mt-2 font-serif text-4xl text-[#0A1128]">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Refer & Earn */}
        <div className="mt-10">
          <HostReferralTile />
        </div>

        {/* Growth */}
        {items.some((p) => p.status === "certified") && (
          <div className="mt-10">
            <HostGrowthTile />
          </div>
        )}

        {/* Alerts feed */}
        {items.length > 0 && (
          <div className="mt-10">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Live Signals</div>
                <h2 className="mt-1 font-serif text-2xl text-[#0A1128]">Recent alerts</h2>
              </div>
              <div className="text-xs text-slate-400">Simulated · IoT-ready</div>
            </div>
            <div className="mt-4">
              <HostAlertsFeed properties={items} />
            </div>
          </div>
        )}

        {/* Property list */}
        <div className="mt-10 space-y-4">
          {items.length === 0 && (
            <div className="bg-white p-10 rounded-3xl border border-dashed border-slate-300 text-center text-slate-500" data-testid="host-empty">
              No properties yet. Add your first stay to begin certification.
            </div>
          )}
          {items.map((p) => {
            const cert = certForProp(p.id);
            return (
              <div key={p.id} className="bg-white rounded-3xl border border-slate-200 overflow-hidden md:flex" data-testid={`host-prop-${p.id}`}>
                <div className="md:w-56 aspect-[4/3] md:aspect-auto bg-slate-100">
                  <img src={p.photos?.[0] || "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800"} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex-1 flex flex-wrap gap-6 items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {p.status === "certified" ? <CertifiedBadge tier={p.certification_tier || "standard"} compact />
                        : <span className="text-xs tracking-[0.2em] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600">{p.status}</span>}
                    </div>
                    <h3 className="mt-2 font-serif text-2xl text-[#0A1128]">{p.name}</h3>
                    <div className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {p.city}, {p.country}</div>
                  </div>
                  <FreshnessGauge score={p.freshness_score} size={110} label="LIVE" />
                  <div className="flex flex-col gap-2 min-w-[180px]">
                    {p.status !== "certified" && !cert && (
                      <>
                        <Button size="sm" onClick={() => applyCert(p.id, "standard")} data-testid={`apply-standard-${p.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                          <ShieldCheck className="w-4 h-4 mr-1" /> Certify · $49
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => applyCert(p.id, "premium")} data-testid={`apply-premium-${p.id}`} className="rounded-full border-[#D4AF37] text-[#0A1128] hover:bg-[#F9F3E5]">
                          Premium · $129
                        </Button>
                      </>
                    )}
                    {cert && cert.status === "pending" && (
                      <span className="text-xs px-3 py-2 rounded-full bg-yellow-50 border border-yellow-200 text-yellow-700 text-center flex items-center justify-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Awaiting admin review
                      </span>
                    )}
                    {p.status === "certified" && p.certification_code && (
                      <BadgeDialog>
                        <BadgeDialogTrigger asChild>
                          <button className="inline-flex items-center justify-center text-xs px-3 py-2 rounded-full bg-[#0A1128] text-white hover:bg-[#1A2A3A] transition-colors" data-testid={`kit-open-${p.id}`}>
                            <Sparkles className="w-3.5 h-3.5 mr-1" /> Marketing kit
                          </button>
                        </BadgeDialogTrigger>
                        <BadgeDialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                          <BadgeDialogHeader><BadgeDialogTitle className="font-serif text-2xl">Marketing kit</BadgeDialogTitle></BadgeDialogHeader>
                          <HostMarketingKit property={p} />
                        </BadgeDialogContent>
                      </BadgeDialog>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteProp(p.id)} data-testid={`delete-${p.id}`} className="text-slate-500 hover:text-red-600">
                      <Trash2 className="w-4 h-4 mr-1" /> Remove
                    </Button>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" hidden data-testid={`photo-input-${p.id}`}
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadPhoto(p.id, f); }} />
                      <span className="inline-flex items-center justify-center text-xs px-3 py-2 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-emerald-700 transition-colors" data-testid={`photo-upload-${p.id}`}>
                        <ImagePlus className="w-3.5 h-3.5 mr-1" /> Upload photo
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
